import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { Coins, Hourglass, CalendarDays, ArrowRight, Wrench, ShieldCheck, IdCard, FileText } from "lucide-react";
import { fmtEUR } from "../../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { sumBy, shiftDay } from "./util";

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const GG = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];

export function IncomeChart({ entries, today }) {
  const [period, setPeriod] = useState("7");
  const data = useMemo(() => {
    if (period === "6m") {
      const out = []; let [y, m] = today.split("-").map(Number);
      for (let i = 0; i < 6; i++) { out.unshift(`${y}-${String(m).padStart(2, "0")}`); m -= 1; if (!m) { m = 12; y -= 1; } }
      return out.map(k => ({ label: MESI[Number(k.slice(5)) - 1], ...sumBy(entries, d => d.startsWith(k)) }));
    }
    const n = Number(period);
    return Array.from({ length: n }, (_, i) => shiftDay(today, i - n + 1)).map(d => {
      const dt = new Date(`${d}T12:00:00Z`);
      return { label: n <= 7 ? `${GG[dt.getUTCDay()]} ${dt.getUTCDate()}` : String(dt.getUTCDate()), ...sumBy(entries, x => x === d) };
    });
  }, [entries, period, today]);
  const total = data.reduce((s, d) => s + d.total, 0);
  return (
    <section className="mb-card p-4 md:p-5 min-w-0" data-testid="mb-income-chart">
      <header className="flex items-center justify-between gap-2 mb-2">
        <h2 className="font-display text-xl text-white">Andamento incassi</h2>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[150px] h-8 bg-black/40 border-white/15 text-[12px]" data-testid="mb-income-period"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-950 border-slate-800"><SelectItem value="7">Ultimi 7 giorni</SelectItem><SelectItem value="30">Ultimi 30 giorni</SelectItem><SelectItem value="6m">Ultimi 6 mesi</SelectItem></SelectContent>
        </Select>
      </header>
      <div className="flex flex-wrap gap-4 text-[11px] text-slate-300 mb-1">
        <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-full bg-[#9DB4E0]" />Cash</span>
        <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-full bg-[#3B5BDB]" />POS</span>
        <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-full bg-slate-500" />Altri metodi</span>
        <span className="inline-flex items-center gap-1.5"><i className="w-3 h-0.5 bg-[#E9C46A]" />Totale</span>
        <span className="ml-auto text-slate-400" data-testid="mb-income-total">Periodo: <b className="text-white font-mono-num">{fmtEUR(total)}</b></span>
      </div>
      <div className="h-[220px]">
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ top: 8, right: 4, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: "#0B0A08", border: "1px solid rgba(212,167,44,0.3)", borderRadius: 8, fontSize: 12 }} formatter={(v, k) => [fmtEUR(v), { cash: "Cash", pos: "POS", other: "Altri", total: "Totale" }[k]]} />
            <Bar dataKey="cash" stackId="a" fill="#9DB4E0" />
            <Bar dataKey="pos" stackId="a" fill="#3B5BDB" />
            <Bar dataKey="other" stackId="a" fill="#64748B" radius={[3, 3, 0, 0]} />
            <Line dataKey="total" stroke="#E9C46A" strokeWidth={2} dot={{ r: 2.5, fill: "#E9C46A" }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

const Item = ({ icon: Icon, v, l, testid }) => (
  <div className="flex items-center gap-3"><Icon size={20} className="text-[#E9C46A] shrink-0" /><div><div className="text-lg text-white font-mono-num leading-tight" data-testid={testid}>{v}</div><div className="text-[11px] text-slate-400">{l}</div></div></div>
);

export function MonthSummary({ inc, due, today }) {
  const [y, m] = today.split("-").map(Number);
  const left = new Date(Date.UTC(y, m, 0)).getUTCDate() - Number(today.slice(8));
  const parts = [{ v: inc.cash, c: "#9DB4E0" }, { v: inc.pos, c: "#3B5BDB" }, { v: inc.other, c: "#E9C46A" }].filter(p => p.v > 0);
  return (
    <section className="mb-card p-4 md:p-5 min-w-0" data-testid="mb-month-summary">
      <h2 className="font-display text-xl text-white">Riepilogo mensile</h2>
      <div className="text-[12px] text-slate-400 capitalize">{new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString("it-IT", { month: "long", year: "numeric" })}</div>
      <div className="grid grid-cols-[150px_1fr] sm:grid-cols-[180px_1fr] items-center gap-4 mt-3">
        <div className="relative h-[150px] sm:h-[180px]">
          <ResponsiveContainer><PieChart><Pie data={parts.length ? parts : [{ v: 1, c: "#1F1B12" }]} dataKey="v" innerRadius="72%" outerRadius="90%" startAngle={90} endAngle={-270} stroke="none">
            {(parts.length ? parts : [{ c: "#1F1B12" }]).map((p, i) => <Cell key={i} fill={p.c} />)}</Pie></PieChart></ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center"><div className="text-[10px] text-slate-400">Incassato</div><div className="text-lg sm:text-xl text-white font-mono-num">{fmtEUR(inc.total)}</div></div>
        </div>
        <div className="space-y-4">
          <Item icon={Coins} v={fmtEUR(inc.total)} l="Incassato nel mese" testid="mb-month-inc" />
          <Item icon={Hourglass} v={fmtEUR(due)} l="Da incassare" testid="mb-month-due" />
          <Item icon={CalendarDays} v={left} l="Giorni rimanenti" testid="mb-month-left" />
        </div>
      </div>
      <div className="text-[10px] text-slate-500 mt-2">Nessun obiettivo mensile configurato: sono mostrati solo i dati effettivi.</div>
    </section>
  );
}

const DIcon = (e) => e.kind === "driver" ? IdCard : /assicur/i.test(e.label || e.type || "") ? ShieldCheck : e.kind === "vehicle" ? Wrench : FileText;
const KIND = { vehicle: "Veicolo", driver: "Autista", company: "Azienda" };
export function Deadlines({ items }) {
  const nav = useNavigate();
  return (
    <section className="mb-card p-4 md:p-5 min-w-0" data-testid="mb-deadlines">
      <header className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl text-white">Scadenze e avvisi</h2>
        <button type="button" onClick={() => nav("/scadenze")} className="h-8 px-3 rounded-full border border-white/20 text-[12px] text-white inline-flex items-center gap-1.5 hover:border-[#D4A72C]/60" data-testid="mb-deadlines-all">Vedi tutti <ArrowRight size={13} /></button>
      </header>
      <div className="space-y-2 max-h-[260px] overflow-y-auto">
        {items.slice(0, 6).map((e, i) => {
          const I = DIcon(e), d = e.days ?? 999;
          const tone = d < 0 ? "text-rose-300 border-rose-500/40 bg-rose-500/10" : d <= 15 ? "text-[#F1D27A] border-[#D4A72C]/45 bg-[#D4A72C]/10" : "text-sky-300 border-sky-500/35 bg-sky-500/10";
          return (
            <button key={i} type="button" onClick={() => nav(e.kind === "vehicle" ? "/flotta" : e.kind === "driver" ? "/autisti" : "/scadenze")} data-testid={`mb-deadline-${i}`} className="w-full flex items-center gap-3 text-left">
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${e.kind === "driver" ? "bg-sky-500/15 text-sky-300" : "bg-[#D4A72C]/15 text-[#E9C46A]"}`}><I size={17} /></span>
              <span className="min-w-0 flex-1"><span className="block text-[12.5px] text-white truncate">{e.label || e.type || e.doc_type} {e.subject}</span><span className="block text-[11px] text-slate-500">{KIND[e.kind] || "Scadenza"}</span></span>
              <span className={`px-2 py-1 rounded-md border text-[11px] whitespace-nowrap ${tone}`}>{d < 0 ? `Scaduta da ${-d} g` : d === 0 ? "Oggi" : `Entro ${d} giorni`}</span>
            </button>
          );
        })}
        {!items.length && <div className="text-sm text-slate-500 text-center py-8" data-testid="mb-deadlines-empty">Nessuna scadenza in arrivo</div>}
      </div>
    </section>
  );
}
